import React, { useState } from 'react';
import { GripVertical, Key, MoreVertical, Search } from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { WORKFLOW_MAIN_COLUMN_WIDTH } from '../../utils/workflowCardAnchors';
import WorkflowStepContextMenu from './WorkflowStepContextMenu';

const GROUP_ICONS = {
  prepare_password_reset: Key,
  find_user_portal: Search,
};

const GroupedWorkflowStepCard = ({
  groupItem,
  isSelected = false,
  isMoving = false,
  highlighted = false,
  readOnly = false,
  hasClipboard = false,
  onEdit,
  onRemove,
  onDragHandleStart,
  onContextMenuAction,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const Icon = GROUP_ICONS[groupItem.groupType] || Key;
  const showHoverChrome = !readOnly && (isSelected || isMoving || highlighted);
  const primaryStepId = groupItem.stepIds[0];

  const handleCardBodyClick = (event) => {
    if (readOnly) return;
    if (event.target.closest('[data-workflow-card-action]')) return;
    onEdit?.(groupItem);
  };

  return (
    <div
      className="group relative mx-auto flex w-full justify-center"
      style={{ width: WORKFLOW_MAIN_COLUMN_WIDTH }}
      data-workflow-step-card
      data-workflow-card
      data-workflow-canvas-node
      data-workflow-card-center-x={WORKFLOW_MAIN_COLUMN_WIDTH / 2}
      data-workflow-group-card
      data-group-type={groupItem.groupType}
      data-testid={`grouped-step-${groupItem.groupType}`}
    >
      {!readOnly ? (
        <button
          type="button"
          aria-label="Move grouped action"
          draggable
          data-workflow-drag-handle
          data-workflow-editor-ignore-pan
          onDragStart={(event) => {
            event.stopPropagation();
            event.dataTransfer.effectAllowed = 'move';
            setIsDragging(true);
            onDragHandleStart?.(primaryStepId);
          }}
          onDragEnd={() => setIsDragging(false)}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'absolute -left-8 top-1/2 z-30 flex h-8 w-8 -translate-y-1/2 cursor-grab items-center justify-center rounded-md border border-transparent bg-white text-slate-400 opacity-0 shadow-sm transition-opacity active:cursor-grabbing group-hover:opacity-100 hover:border-slate-200 hover:text-slate-600',
            (showHoverChrome || isDragging) && 'opacity-100',
          )}
        >
          <GripVertical size={16} />
        </button>
      ) : null}

      <div
        role="button"
        tabIndex={readOnly ? -1 : 0}
        data-workflow-card-body
        onClick={handleCardBodyClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleCardBodyClick(event);
          }
        }}
        className={cn(
          'group/card flex min-h-[76px] w-full cursor-pointer items-center gap-3 rounded-[18px] border px-[18px] py-4 shadow-sm transition-all',
          'border-emerald-200 bg-emerald-50/80 hover:shadow-md',
          isSelected && 'border-emerald-600 ring-2 ring-emerald-600/15',
          highlighted && 'border-emerald-500 ring-2 ring-emerald-500/20',
        )}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
          <Icon size={20} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-[15px] font-semibold text-slate-900">{groupItem.title}</p>
            {groupItem.badge ? (
              <Badge variant="outline" className="border-emerald-200 bg-white text-emerald-700 text-[10px]">
                {groupItem.badge}
              </Badge>
            ) : null}
            {groupItem.attentionBadge ? (
              <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700 text-[10px]">
                {groupItem.attentionBadge}
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-slate-600">{groupItem.subtitle}</p>
          {groupItem.summary ? (
            <p className="mt-1 text-xs text-emerald-700">{groupItem.summary}</p>
          ) : null}
        </div>

        {!readOnly ? (
          <WorkflowStepContextMenu
            hasClipboard={hasClipboard}
            onAction={(action) => onContextMenuAction?.(action, primaryStepId)}
          >
            <button
              type="button"
              data-workflow-card-action
              data-workflow-editor-ignore-pan
              data-testid="grouped-step-context-menu-trigger"
              className="rounded p-1 text-slate-400 hover:bg-white hover:text-slate-700"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreVertical size={16} />
            </button>
          </WorkflowStepContextMenu>
        ) : null}
      </div>
    </div>
  );
};

export default GroupedWorkflowStepCard;
